import { DropZone } from "@heroui-pro/react";
import { Spinner } from "@heroui/react";
import { RefreshCw, Upload } from "lucide-react";

const DEFAULT_TYPES = ["image/png", "image/jpeg", "image/webp"];

function Picker({
  children,
  className,
  isDisabled,
  onDropFiles,
}: {
  children: React.ReactNode;
  className?: string;
  isDisabled?: boolean;
  onDropFiles: (files: File[]) => void;
}) {
  return (
    <DropZone.Area
      className={className}
      isDisabled={isDisabled}
      onDrop={async (event) => {
        const items = event.items.filter((item) => item.kind === "file");
        const files = await Promise.all(
          items.map((item) =>
            (item as { getFile: () => Promise<File> }).getFile(),
          ),
        );

        onDropFiles(files);
      }}
    >
      {children}
      {/* The whole area is the click target that opens the file picker. */}
      <DropZone.Trigger
        aria-label="Select image"
        className="absolute inset-0 z-10 cursor-(--cursor-interactive) rounded-[inherit] opacity-0"
        isDisabled={isDisabled}
      >
        {""}
      </DropZone.Trigger>
    </DropZone.Area>
  );
}

/**
 * The image DropZone from the boards. It hands the dropped or picked file to `onFile`: where the file goes (the banner
 * upload endpoint today) is the caller's call. With `value` it shows that image with a Replace chip; `isUploading`
 * covers it with a spinner; `errorMessage` shows under it.
 */
export function ImageDropZone({
  label,
  onFile,
  accept = DEFAULT_TYPES,
  hint = "PNG, JPG or WebP",
  isDisabled,
  value,
  isUploading,
  errorMessage,
  aspectClassName,
}: {
  label: string;
  onFile: (file: File) => void;
  accept?: string[];
  hint?: string;
  isDisabled?: boolean;
  /** An image to show instead of the prompt. */
  value?: string;
  isUploading?: boolean;
  errorMessage?: string | null;
  /** Fixed shape for the area, for example `aspect-[2/1]`. */
  aspectClassName?: string;
}) {
  // Types are checked by the caller, which has a message for them; this only picks the first file.
  const take = (files: FileList | File[]) => {
    const file = Array.from(files)[0];

    if (file) onFile(file);
  };

  return (
    <DropZone aria-label={label}>
      <Picker
        className={`relative overflow-hidden ${aspectClassName ?? ""} ${
          errorMessage ? "border-(--danger)" : ""
        }`}
        isDisabled={isDisabled || isUploading}
        onDropFiles={take}
      >
        {value ? (
          <>
            <img
              alt={`${label} preview`}
              className="absolute inset-0 size-full object-cover"
              src={value}
            />
            <span className="bg-overlay shadow-overlay absolute bottom-3 right-3 flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium">
              <RefreshCw className="size-3.5" />
              Replace
            </span>
          </>
        ) : (
          <>
            <DropZone.Icon>
              <Upload className="size-5" />
            </DropZone.Icon>
            <DropZone.Label>
              Drag and drop an image here, or click to select
            </DropZone.Label>
            <DropZone.Description>{hint}</DropZone.Description>
          </>
        )}
        {isUploading ? (
          <div className="bg-surface/70 absolute inset-0 flex flex-col items-center justify-center gap-2 backdrop-blur-sm">
            <Spinner size="sm" />
            <span className="text-muted text-xs">Uploading...</span>
          </div>
        ) : null}
        <DropZone.Input accept={accept.join(",")} onSelect={take} />
      </Picker>
      {errorMessage ? (
        <p className="text-(--danger) text-sm" role="alert">
          {errorMessage}
        </p>
      ) : null}
    </DropZone>
  );
}
